/**
 * The handful of values every main-toolbar action hook needs, resolved once
 * in `MainToolbar` and threaded down. Keeping them in one object is what let
 * the ~700 lines of click handlers move out of the component verbatim.
 */
import { useMemo } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { useWorkspaceStore } from '../../workspace';
import { useContextSelectionStore } from '../../toolbar-context';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import type { ActiveConnection } from '../../connections/models/connectionTypes';

type RequestContext = ReturnType<typeof useContextSelectionStore.getState>['requestContext'];
type AddTab = ReturnType<typeof useWorkspaceStore.getState>['addTab'];
type ValidDbSelection = ReturnType<
  ReturnType<typeof useDatabaseUIStore.getState>['getValidSelectedDatabase']
>;

export type ToolbarActionContext = {
  track: TrackFunction;
  requestContext: RequestContext;
  addTab: AddTab;
  getNextQueryNumber: () => number;
  getNextNotebookNumber: () => number;
  validDbSelection: ValidDbSelection;
  activeConnections: ActiveConnection[];
};

export function useToolbarActionContext(): ToolbarActionContext {
  const { track } = useAnalytics();
  const { getNextQueryNumber, getNextNotebookNumber, addTab } = useWorkspaceStore();
  const requestContext = useContextSelectionStore((s) => s.requestContext);
  const { getValidSelectedDatabase } = useDatabaseUIStore();
  const { activeConnections } = useConnectionStore();
  const validDbSelection = getValidSelectedDatabase();

  return useMemo(
    () => ({
      track,
      requestContext,
      addTab,
      getNextQueryNumber,
      getNextNotebookNumber,
      validDbSelection,
      activeConnections,
    }),
    [
      track,
      requestContext,
      addTab,
      getNextQueryNumber,
      getNextNotebookNumber,
      validDbSelection,
      activeConnections,
    ],
  );
}
